/*
 * Copyright (c) 2020 - 2025 Ping Identity Corporation. All rights reserved.
 *
 * This software may be modified and distributed under the terms
 * of the MIT license. See the LICENSE file for details.
 */
import { initQuery } from '@forgerock/sdk-request-middleware';
import { REQUESTED_WITH, getEndpointPath, stringify, resolve } from '@forgerock/sdk-utilities';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query';
function constructUrl(serverConfig, tree, query) {
    const treeParams = tree ? { authIndexType: 'service', authIndexValue: tree } : undefined;
    const params = { ...query, ...treeParams };
    const queryString = Object.keys(params).length > 0 ? `?${stringify(params)}` : '';
    const path = getEndpointPath({
        endpoint: 'authenticate',
        customPaths: serverConfig.paths,
    });
    const url = resolve(serverConfig.baseUrl, `${path}${queryString}`);
    return url;
}
function constructSessionsUrl(serverConfig, query) {
    const params = { ...query };
    const queryString = Object.keys(params).length > 0 ? `?${stringify(params)}` : '';
    const path = getEndpointPath({
        endpoint: 'sessions',
        customPaths: serverConfig.paths,
    });
    const url = resolve(serverConfig.baseUrl, `${path}${queryString}`);
    return url;
}
function configureRequest(step) {
    const init = {
        body: step ? JSON.stringify(step.payload) : undefined,
        credentials: 'include',
        headers: new Headers({}),
        method: 'POST',
    };
    return init;
}
function configureSessionRequest() {
    const init = {
        credentials: 'include',
        headers: new Headers({}),
        method: 'POST',
    };
    return init;
}
export const journeyApi = createApi({
    reducerPath: 'journeyReducer',
    baseQuery: fetchBaseQuery({
        baseUrl: '/',
        prepareHeaders: (headers) => {
            headers.set('Accept', 'application/json');
            headers.set('Accept-API-Version', 'protocol=1.0,resource=2.1');
            headers.set('Content-Type', 'application/json');
            headers.set('X-Requested-With', REQUESTED_WITH);
            return headers;
        },
    }),
    endpoints: (builder) => ({
        start: builder.mutation({
            queryFn: async (options, api, _, baseQuery) => {
                const state = api.getState();
                const { serverConfig } = state.config;
                if (!serverConfig) {
                    throw new Error('Server configuration is missing.');
                }
                const query = options?.query || {};
                const url = constructUrl(serverConfig, options?.journey, query);
                const request = configureRequest();
                const { requestMiddleware } = api.extra;
                const response = await initQuery({ ...request, url: url }, 'begin', {
                    type: 'service',
                    tree: options?.journey,
                })
                    .applyMiddleware(requestMiddleware)
                    .applyQuery(async (req) => {
                    const result = await baseQuery(req, api, api.extra);
                    return result;
                });
                return response;
            },
        }),
        next: builder.mutation({
            queryFn: async ({ step, options }, api, _, baseQuery) => {
                const state = api.getState();
                const { serverConfig } = state.config;
                if (!serverConfig) {
                    throw new Error('Server configuration is missing.');
                }
                const query = options?.query || {};
                const url = constructUrl(serverConfig, undefined, query);
                const request = configureRequest(step);
                const { requestMiddleware } = api.extra;
                const response = await initQuery({ ...request, url }, 'continue')
                    .applyMiddleware(requestMiddleware)
                    .applyQuery(async (req) => {
                    const result = await baseQuery(req, api, api.extra);
                    return result;
                });
                return response;
            },
        }),
        terminate: builder.mutation({
            queryFn: async (options, api, _, baseQuery) => {
                const state = api.getState();
                const { serverConfig } = state.config;
                if (!serverConfig) {
                    throw new Error('Server configuration is missing.');
                }
                const query = { ...options?.query, _action: 'logout' };
                const url = constructSessionsUrl(serverConfig, query);
                const request = configureSessionRequest();
                const { requestMiddleware } = api.extra;
                const response = await initQuery({ ...request, url }, 'terminate')
                    .applyMiddleware(requestMiddleware)
                    .applyQuery(async (req) => {
                    const result = await baseQuery(req, api, api.extra);
                    return result;
                });
                return response;
            },
        }),
    }),
});
